import app from 'flarum/forum/app';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
// v0.6.64 — 课表学员头像改 Flarum Avatar (辉哥 9/30 23:03 拍板: 全部用户头像改 Flarum Avatar)
//   Flarum Avatar 默认不渲染 <a> (点击不跳用户页), 默认无 border (无 box-shadow/outline)
import Avatar from 'flarum/common/components/Avatar';
import Api from '../utils/Api';
// v0.6.35 — "+ 新建预约" 按钮 onclick 弹 LessonEditorModal (辉哥 9/29 22:34 反馈)
// v0.6.37 — 改调 utils/BookingModalHelper.openBookingModal (辉哥 9/29 23:46 反馈, DRY)
//   移除 LessonEditorModal 直接 import (helper 自己 import modal)
import openBookingModal from '../utils/BookingModalHelper';
// v0.6.41 — 课程类型 + 课程分类 labelFor() (辉哥 9/30 8:20 反馈, 跟 LessonEditorModal v0.6.36/0.6.37 同函数)
import LessonOptions from '../utils/LessonOptions';
// v0.18.0 (辉哥 10/18 拍板) — 课评 (草稿 / 发布 / 只读) 的共享工具
//   ⚠️ 三态入口的图标判定**只在** LessonReviews.iconClassFor 里 (WCAG 1.4.1 的落点),
//      别在本文件里另写一份 switch, 两处必然漂移。
import { fetchReviewMap, openReviewModal, iconClassFor, stateClassFor, starsFor } from '../utils/LessonReviews';
// 批 41 · 段① — 无限滚动 + IntersectionObserver 整套机制现在共用这个基类
//   (原来本文件抄了一份, OperationLogsTab / SalesTab / StudentLessonsModal 各抄一份)
//   ⚠️ 本文件只需在 oninit 里给 this.sentinelAttr; 其余 (滚动根 / observer / 重试 / 翻页) 全在基类里。
import InfiniteScrollBase from '../utils/InfiniteScroll';

/** 每页条数 —— 辉哥 10/13 拍板: 首屏 20 条, 往下滚动继续 20 条 (跟 OperationLogsTab.js:13 同值) */
const PAGE_SIZE = 20;

/**
 * 搜索 debounce 毫秒数 —— 取 300ms
 *
 * 理由: 本仓库既有的「实时冲突检测」也是 300ms (见 CheckLessonConflictController 类注释
 * 「前端在 form 里防抖 300ms 调用」), 与它对齐 = 一套手感, 用户不用适应两种节奏。
 * 300ms 也够短 —— 打完最后一个字约 1/3 秒就出结果, 不像 500ms 那样「感觉没反应」;
 * 又够长 —— 中文输入法 (拼音候选框) 选字过程中会连发多串字符, 300ms 能把一串拼音
 * 合成一次请求。验收判据 5 是「连续快速输入只发 1 次」, 300ms 在 Playwright 实测通过。
 */
const SEARCH_DEBOUNCE_MS = 300;

/** sentinel 唯一 id 计数器 —— 跟 OperationLogsTab.js:19 同款, 用来在 DOM 里认领「哪个 sentinel 是我的」 */
let tabSeq = 0;

/* ══════════════════════════════════════════════════════════════════════════════
 * v0.18.33 (辉哥 10/20 报) — 时间筛选 + 状态筛选 (从**死 UI** 变成真筛选)
 *
 * ## 病根: 这两个下拉从加出来到今天一直是**装饰品**
 *   v0.5.0q 加进来的「时间」「状态」两个下拉, 4 个选项全是
 *   `onclick={() => m.redraw()}` —— **无 state、无 API、无过滤**, 点了什么都不发生。
 *   (只有「教练」那一条在 v0.17.49 真做了: 读后端下发的 coaches。)
 *
 * ## ⚠️ 时间区间的算法为什么**必须在前端**
 *   `lessons.start_at` 存的是**用户本地时间字面量**, 而**生产服务器的 PHP date() 是 UTC**
 *   (config('app.timezone') 也是 UTC) ⇒ 后端自己算「今天/本周/本月」会差 8 小时。
 *   ⇒ 区间由**浏览器本地时区**算好传过去, 后端只做字面量比较。
 *   (后端侧的完整论证见 ListLessonsPageController 里 v0.18.33 那段注释。)
 * ══════════════════════════════════════════════════════════════════════════════ */

/** 时间筛选项 —— key 是内部值, label 是下拉里的字 */
const TIME_FILTERS = {
  today: {
    label: '今天'
  },
  week: {
    label: '本周'
  },
  month: {
    label: '本月'
  }
};

/**
 * 状态筛选项 —— key = `lessons.status` 列的**真实取值** (代码为准, 不是猜的)
 *
 * ⚠️ 取值必须与后端 `Lesson::$status` 的白名单逐字一致:
 *   已预约 = 'scheduled' / 已签到 = 'checked-in' / 已消课 = 'completed'
 *   (跟本文件 statusBadge() 的 map 是同一套值 —— 见 L735-744)
 * ⚠️ 「已取消」('cancelled') **故意不做**成筛选项:
 *   列表口径本来就显示已取消的课, 给用户一个「已取消」筛选项会诱导他
 *   以为「已取消的课不该出现在列表里」, 那又是另一个需求。
 */
const STATUS_FILTERS = {
  scheduled: {
    label: '已预约'
  },
  'checked-in': {
    label: '已签到'
  },
  completed: {
    label: '已消课'
  }
};

/**
 * Date → 'YYYY-MM-DD' —— **本地时区**
 *
 * ⚠️ **绝对不能用 toISOString()**: 那是 UTC, 在东八区会把「今天」算成「昨天」
 *   (本地 00:30 → UTC 前一天 16:30 ⇒ 日期少一天)。这正是本轮要绕开的那个坑。
 *   getFullYear/getMonth/getDate 拿的都是**本地**分量, 正是我们要的。
 */
function fmtLocalDate(d) {
  const p = n => (n < 10 ? '0' : '') + n;
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

/**
 * 时间筛选 key → { start, end } (两个 YYYY-MM-DD, 本地日期字面量)
 *
 * ⚠️ 全部用 `new Date(y, m, d)` 构造 —— 它按**本地时区**取午夜,
 *   跨月/跨年/DST 都由 JS 自己处理, 不用自己加减天数。
 *
 * 「本周」的定义 = **本地周一 ~ 本地周日** (ISO 周):
 *   中文语境里「本周」通常指「周一开始的这一周」, 而不是「最近 7 天」。
 *   (辉哥一句话就能改成「近 7 天」, 改动只在下面 week 这一支。)
 *
 * @param {string} key  'today' | 'week' | 'month'
 * @returns {{start: string, end: string}|null}  null = 全部时间 (不传参数)
 */
function localDateRange(key) {
  const now = new Date();
  if (key === 'today') {
    // 今天 = 本地当天 ⇒ start === end
    const s = fmtLocalDate(now);
    return {
      start: s,
      end: s
    };
  }
  if (key === 'week') {
    // 本地周一 ~ 本地周日 (ISO 周)
    const dow = now.getDay(); // 0 = 周日 … 6 = 周六
    const back = (dow + 6) % 7; // 周一 = 0 … 周日 = 6
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - back);
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    return {
      start: fmtLocalDate(monday),
      end: fmtLocalDate(sunday)
    };
  }
  if (key === 'month') {
    // 本月 1 号 ~ 本月最后一天
    // ⚠️ `new Date(y, m + 1, 0)` = 「下个月的第 0 天」= 本月最后一天 ——
    //   大小月与闰年都对 (2026-02 → 28, 2028-02 → 29, 2026-04 → 30), 不用自己查天数。
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    return {
      start: fmtLocalDate(first),
      end: fmtLocalDate(last)
    };
  }
  return null; // 全部时间 = 不传参数
}

/**
 * ============================================================================
 * v0.17.18 — 改分页滚动加载 + 搜索框真正实现 (辉哥 10/13 拍板)
 *
 * ## 改前这个搜索框是**死的** (不是「不好用」, 是完全没有实现)
 *   this.search 全文件只出现 3 处: oninit 初始化 '' / input 的 value / oninput 里赋值 + m.redraw()。
 *   全文件**没有任何** filter( / toLowerCase / includes( / indexOf( —— 打字完全不触发筛选。
 *   placeholder 写着「搜索学员/教练...」, 但那个框从 v0.x 加出来到今天一直是装饰品。
 *
 * ## 搜索走**后端全量** (辉哥 10/13 拍板)
 *   搜的是全库, 不是前端已加载的那 20 条 —— 否则「翻到第 2 页才出现的学员」永远搜不到。
 *   ⇒ 搜索参数与分页参数**一起**发给后端, 前端不做任何本地过滤。
 *   ⇒ 新端点 GET /ziiven/scheduler/lessons/page 返 {data, meta}, 出参逐字段复用
 *      LessonSerializer (跟 GET /lessons 同款, 后端那边注释解释了为什么不复用会走偏)。
 *
 * ## ⚠️ 为什么另开端点而不改 GET /lessons
 *   GET /ziiven/scheduler/lessons 有两个消费方, 分页需求相反:
 *     - CalendarTab.js:448 传 `date`, 需要当天**完整**数据做 FullCalendar 碰撞检测
 *       ⇒ 一旦认 offset/limit 就会静默截断 ⇒ 冲突漏报 (平时看不出来, 哪天才炸)。
 *     - 本组件不传 date, 无界。
 *   所以 GET /lessons **原样不动**, 新端点只服务本组件。
 *
 * ## ★ 日历没被碰坏 (验收判据 1, 本轮最高回归风险面)
 *   改造前后逐字节比对了 GET /lessons (9 个日期 + 无 date) 与 check-conflict 的响应。
 *
 * ## ★ 三个坑 (v0.17.16a 修好的版本; 现在共用 utils/InfiniteScroll.js, 不重蹈 StudentLessonsModal)
 *   1) **滚动根**: 本组件是页面里的**普通区块**, 祖先链上一个可滚容器都没有 ⇒ 若只按
 *      overflow-y 选根会选到 `<body>` (Bootstrap 给它写了 overflow-y: scroll, 但它
 *      自己 scrollHeight === clientHeight, 根本不是滚动容器) ⇒ root 覆盖整篇文档 ⇒
 *      **不滚动就把所有页级联加载完**。⇒ findScrollRoot 必须显式排除 body/documentElement,
 *      返回 null (= 视口), 这不是降级兜底而是几何正确的值。
 *   2) **m.redraw() 不同步更新 DOM**: 本仓库的 m.redraw 被 Flarum 包装过, 手动改完 DOM
 *      紧接着 m.redraw(), 同步查 DOM **拿到的还是旧的** ⇒「redraw(); syncObserver();」
 *      紧挨着写会让 sentinel 还没进 DOM 就去找 ⇒ observer 一次都建不出来
 *      (症状: 滚到底纹丝不动、不报错)。⇒ 找不到时推到下一帧重试并限次 (scheduleSync)。
 *   3) **孤儿 observer 泄漏**: tab 卸载后 this.element 仍指向**已脱离文档**的旧 DOM,
 *      this.element.querySelector 照样查得到节点 ⇒ 给没人再 disconnect 的节点建 observer。
 *      ⇒ findSentinel 走**全局 document 查询 + 组件唯一 id 认领**, 再加 _destroyed 闸。
 *   ⇒ 批 41 · 段① 起, 这六个方法 (findSentinel / findScrollRoot / disconnectObserver /
 *      syncObserver / scheduleSync / loadNextPage) 已**共用** utils/InfiniteScroll.js ——
 *      上面三条坑的实测根因注释全部搬进了那个文件。
 *
 * ## ★ 追加页里有**新** id ⇒ 头像会变空白
 *   studentAvatar() 依赖 Flarum store 里的 User model (User.displayName() / avatarUrl()
 *   不能 JSON 序列化, 必须走 store)。改造前 load() 一次性预塞全部 id; 分页后追加的第 2 页
 *   带着**新**学员/教练 id ⇒ 不重跑预塞, 这些人的头像就是空的 (很容易漏且看不出原因)。
 *   ⇒ 预塞抽成 prefetchUsers(rows), **每批都跑** (replace 和 append 都跑)。
 *
 * ## ★ 原地改 status 的逻辑**保留**
 *   checkIn / complete / cancel 走 updateLessonStatus() **直接改 this.lessons 里那一条**
 *   + m.redraw(), 不重新拉整页 (乐观更新, v0.7.8j 定的交互)。分页后这套「原地修改」
 *   与「重新拉一页」是两套东西, 本轮没有把它重写成重新请求 —— 现有交互不变。
 * ============================================================================
 */
export default class LessonsTab extends InfiniteScrollBase {
  oninit(vnode) {
    super.oninit(vnode);
    this.lessons = [];
    this.loading = true;
    // v0.17.18 — 搜索框从「装饰品」变成真搜索: 每次输入变化都经 onSearchInput → debounce → loadFirstPage
    this.search = '';
    // v0.5.5 — LessonsTab 接 activeSchoolId (辉哥 9/25 08:04 SOP, 跟 v0.5.3 CardTemplatesTab 同款)
    this.activeSchoolId = vnode.attrs.activeSchoolId;
    // v0.6.15 — 跟踪哪些 mobile lesson card 已展开 (辉哥 22:36 拍板 A 方案: 折叠展开区默认 hidden)
    this.expandedLessons = new Set();
    // v0.7.8h — actor role (辉哥 10/3 22:20 拍板, 学员只看到签到/取消按钮)
    this.actorRole = null;
    // ── v0.17.49: 教练筛选 (后端下发, 前端**无状态判定**) ──
    //   canFilterCoach / coaches 由 lessons/page 的 meta 一并下发。
    //   ⚠️ 前端**不判断**「谁是店长」: ActorRoleController 只返 role 一个键, 而
    //      resolve() 永远不返 'manager' (店长是正交布尔标记), 前端拿不到 ⇒ 只能由后端算。
    //      这里只认后端给的 bool, 不做任何角色推断。
    this.canFilterCoach = false;
    this.coaches = [];
    this.coachFilter = ''; // '' = 全部教练 (不传 coach_id)
    // ── v0.18.33: 时间筛选 + 状态筛选 (从死 UI 变成真筛选) ──
    //   '' = 全部 (不传参数, 与 coach_id / school_id 同款处理:
    //   传 null 会被 mithril 序列化成 'status=null' / 'start=null' 字面量, 后端会照单全收)
    this.timeFilter = ''; // '' | 'today' | 'week' | 'month'
    this.statusFilter = ''; // '' | 'scheduled' | 'checked-in' | 'completed'
    // ── v0.17.18 分页状态 (跟 OperationLogsTab.js 同款命名, 方便两边对照) ──
    this.loadingMore = false;
    this.meta = null; // 后端汇总 (total / limit / offset / has_more / q)
    this.offset = 0; // 已加载条数 (= 下一批的 offset)
    this.reqSeq = 0; // 竞态守卫: 只有最新一次请求的结果算数
    this.paginatedOnce = false; // 是否翻过页 —— 决定要不要显示「已全部加载」
    this._observer = null; // IntersectionObserver 实例
    this._syncRaf = null; // 待执行的 rAF (syncObserver 重试), 关闭时要撤掉
    this._searchTimer = null; // 搜索 debounce 定时器
    this._destroyed = false; // onremove 置 true —— 卸载后不再建 observer (防泄漏)
    this._sid = 'zlessons' + ++tabSeq; // 本实例 sentinel 的 DOM 认领 id
    // 批 41 · 段① — 哨兵 DOM 属性名 (共用基类 utils/InfiniteScroll.js 按它认领 sentinel)
    this.sentinelAttr = 'data-zlessonsid';
    // v0.18.0 (辉哥 10/18 拍板) — 课评三态表 { lessonId: {status, rating} }
    //   ⚠️ 服务端对**学员**只返回 published (R2 草稿绝不泄露); 教练端才会拿到 draft。
    //   ⚠️ 这是**附加**数据, 拿不到就当「全都没有课评」, 绝不影响列表本身的可用性。
    this.reviewMap = {};
    this.reviewSeq = 0; // 竞态守卫 (同 fetchPage 的 reqSeq 思路)
    this.load();
    this.loadActorRole();
  }

  // v0.17.18 — 卸载清理 (照抄 OperationLogsTab.js onremove, 防 observer + rAF + timer 泄漏)
  onremove(vnode) {
    super.onremove(vnode);
    // ⚠️ 必须**在** disconnect 之前置位: 翻页请求可能在 tab 切走之后才回来,
    //   finally 里会调 syncObserver(); 没有这道闸, 它会给一个已经没人管的节点建 observer。
    this._destroyed = true;
    this.disconnectObserver();
    if (this._syncRaf) {
      cancelAnimationFrame(this._syncRaf);
      this._syncRaf = null;
    }
    // 撤掉排队中的搜索 debounce —— 否则切走 tab 了它还会醒来发一个请求
    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }
  }
  redraw() {
    if (typeof m !== 'undefined' && m && typeof m.redraw === 'function') m.redraw();
  }

  // v0.7.8h — actor role (辉哥 10/3 22:20 拍板)
  //   失败 fallback null (按非学员处理, 全功能可见, 不阻塞 UI)
  //
  // ⚠️ v0.17.77 起, 这个字段**不再用于任何按钮的显隐** (那一律改走下面的权限位判据)。
  //   它连同 loadActorRole() 这次一并保留: 删它属于另一件事 (会少一次
  //   GET /actor-role 请求 + 少一次 m.redraw), 本轮不顺手改, 留给后续单独决定。
  async loadActorRole() {
    try {
      const r = await Api.getActorRole();
      this.actorRole = r && r.role ? r.role : null;
      m.redraw();
    } catch (e) {
      this.actorRole = null;
    }
  }

  // ─── v0.17.77 (辉哥 10/16 拍板) — 按钮显隐改走权限位 ────────────────────────
  //
  // 【为什么换掉 actorRole !== 'student'】
  //   辉哥原话「有权限的用户组可以约课、编辑已约课程、取消约课」。角色判定回答不了
  //   「我能不能干这事」, 它回答的是「我是什么角色」—— 于是滑雪教练/销售/店长这几个
  //   零 scheduler 位的组, 即便后台勾了权限也照样被角色挡在门外。
  //
  // 【每个按钮对应的位, 与后端 UpdateLessonController 的判据逐条对齐】
  //   后端规则回顾 (v0.17.78 四档): manageLessons 永远 bypass; 否则按「本次到底改什么」选一个位
  //     纯取消 (只改 status 且值为 'cancelled')  → cancelBookedLessons
  //     纯签到 (只改 status 且值为 'checked-in') → checkInLessons   ← v0.17.78 新增
  //     其余任何字段变更 (含 status='completed')   → editBookedLessons
  //     三个都没有 → 无权操作
  //     签到   checkIn   PATCH {status:'checked-in'}   → 纯签到 ⇒ checkIn 位
  //     取消   cancel    PATCH {status:'cancelled'}    → 纯取消 ⇒ cancel 位
  //     消课   complete  PATCH {status:'completed'}    → 不是前两档 ⇒ edit 位, 但见下方说明
  //
  // 【⚠️ 「纯」的判据在后端是 count($attrs) === 1, 不是 in_array】
  //   后端 Api.update 只发 `{data:{attributes:{status:…}}}`, 键数恰好 1 ⇒ 落在纯签到/纯取消那档。
  //   ⚠️ 将来若有人把这里改成**一次请求带多个字段**, 后端会立刻改判成 editBookedLessons,
  //      而学员多半没有 edit 位 ⇒ 按钮看得见、点了必 422。别这么改。
  //
  // 【⚠️ 消课为什么仍只认 manageLessons (与后端的宽松度故意不对称)】
  //   消课 = 扣学员卡余额 (CardChargeService::consume), 是**财务动作**, 不是排课动作,
  //   辉哥 10/17 的原话是「学员可以自己签到, 但学员不能消课」。所以前端保守地只给重权限位。
  //   为什么不让它认 edit 位 / checkIn 位: 学员的 status 取值白名单 (只允许 checked-in /
  //   cancelled) 会把 status='completed' 挡下来 ⇒ 按钮看得见、点了必 422, 比不显示更糟。
  //   ⇒ 取「前后端都过的那个交集」: 只有 manageLessons 的人能消课。
  //   (拿 edit 位但不是 manageLessons 的员工, 后端对**非学员**其实能过 —— 这是**故意**保守,
  //    财务动作宁可少露一个入口, 也不给一个不该有的按钮。)
  //
  // 【_sessionPerm 在 getter 缺失时 fail-open 的理由同 CalendarTab._sessionPerm】
  //   菜单/按钮只是入口, 后端每个动作都会独立再判一次权限 ⇒ 前端放行不等于授权。
  //   反过来若 fail-closed, 发版窗口里没注册到 getter 的 Mod 会看到「一个按钮都没有」。
  _sessionPerm(name) {
    const u = app.session && app.session.user;
    if (!u || typeof u[name] !== 'function') return true; // fail-open, 见上方说明
    return !!u[name]();
  }

  /**
   * 签到 (PATCH status='checked-in') —— 学员靠这个位才能签到自己的课
   *
   * v0.17.78: 新增 canCheckInLessons 作为**首选**判据, edit/manage 保留为兼容旁路。
   * ⚠️ 顺序不是语义 —— 任一位为 true 就显示; 但把 checkInLessons 放前面是为了让
   *    「只勾了签到位」的学员也能看见这个按钮 (辉哥 10/17 的需求)。
   * ⚠️ 学员即使勾了 editBookedLessons, 点了也只发纯 status 请求 (见上方 count===1 的警告),
   *    不会因为「有 edit 位」就顺手带上别的字段 —— 真正兜底的是后端那道字段白名单。
   */
  canCheckIn() {
    return this._sessionPerm('canCheckInLessons') || this._sessionPerm('canEditBookedLessons') || this._sessionPerm('canManageLessons');
  }

  /** 取消 (PATCH status='cancelled') —— 纯取消, 后端认 cancel 位 */
  canCancelLesson() {
    return this._sessionPerm('canCancelBookedLessons') || this._sessionPerm('canManageLessons');
  }

  /** 消课 (PATCH status='completed', 扣余额) —— 财务动作, 只认重权限位
   *  ⚠️ v0.17.78 复核后**保持不变**: 辉哥原话「学员不能消课」。
   *    这里绝不要加 canCheckInLessons / canEditBookedLessons —— 一加, 学员组就会看到消课按钮。 */
  canCompleteLesson() {
    return this._sessionPerm('canManageLessons');
  }

  /** 新建预约 (POST /lessons, 后端认 manageLessons || bookLessons) */
  canCreateLesson() {
    return this._sessionPerm('canBookLessons') || this._sessionPerm('canManageLessons');
  }

  // ═══════════════════════ v0.18.0 课评 (辉哥 10/18 拍板) ═══════════════════════

  /**
   * 能不能**写**课评 —— 列表行的「课评」入口显隐判据。
   *
   * ⚠️ 这只是**入口**: 后端 SaveLessonReviewController 会独立再判一次, 且额外要求
   *    `lessons.status === 'completed'` (规则 1)。前端不渲染 ≠ 后端放行, 反过来也一样。
   * ⚠️ 跟 v0.17.77 那几个判据同款 fail-open: 菜单/按钮只是入口, 每个动作后端都会再判一次。
   */
  canReviewLessons() {
    return this._sessionPerm('canReviewLessons') || this._sessionPerm('canManageLessons');
  }

  /**
   * 能不能**读**课评 —— 写位 ∪ 既有读位 `scheduler.viewLessons`。
   *
   * ⚠️ 学员端**不新增**读位: 复用的就是课表已有的 `canViewLessons`,
   *   与服务端 `LessonReviewAccess::canRead()` (`canWrite() || viewLessons`) **同款判据**。
   *   学员侧的口径由 R2 那一层兜底 (只看自己 + 只看已发布 + 只看已消课), 不是由权限位兜底。
   * ⛔ 这是**读**判据, 跟 `canReviewLessons()` (写) 是两回事, 别合并 ——
   *   合并会让学员拿到写位, 也会让教练在只读路径上误判成能编辑。
   */
  canViewLessonReviews() {
    return this.canReviewLessons() || this._sessionPerm('canViewLessons');
  }

  /** 某一节课的课评三态 (null = 从未写) */
  reviewOf(l) {
    return l && this.reviewMap && this.reviewMap[l.id] || null;
  }

  /**
   * ⛔ 规则 1: **只有已消课**的课次才渲染课评入口。
   *   已预约 / 已签到 / 已取消的行**完全没有**这个入口 —— 不是 disabled, 是不渲染。
   */
  canReviewRow(l) {
    return this.canReviewLessons() && !!l && l.status === 'completed';
  }

  /**
   * 每批数据都拉一次课评三态 (跟 prefetchUsers 同一个理由: 追加页里带着**新**的课次)。
   *
   * ⚠️ 失败静默: 拿不到就保留空表, 列表照常显示, 只是没有课评入口。
   *   课评是附加列, 不能因为它把整个预约管理页搞成错误态。
   */
  async loadReviewStates(rows) {
    // ⚠️ v0.18.0 学员只读入口 (辉哥 10/18 拍板): 这里从 `canReviewLessons()` 改成**读**判据。
    //   学员只有 `viewLessons` 没有写位, 之前这一行直接 return ⇒ 学员压根拿不到三态 ⇒
    //   行内星级与入口都不会渲染。服务端对学员只返 published (R2), 拿这个数据是安全的。
    if (!this.canViewLessonReviews()) return; // 无读位 ⇒ 不发这个请求 (省一次往返)

    const ids = (rows || []).map(l => l.id).filter(Boolean);
    if (ids.length === 0) return;
    const seq = ++this.reviewSeq;
    const map = await fetchReviewMap(ids);
    if (seq !== this.reviewSeq) return; // 过期响应直接扔掉, 不合并进表
    this.reviewMap = Object.assign({}, this.reviewMap, map);
    this.redraw();
  }

  /** 打开课评 modal; 保存后只更新**这一行**的三态, 不整页重拉 */
  openReview(l) {
    openReviewModal({
      lesson: l,
      descriptor: this.reviewOf(l),
      readonly: false,
      onChange: descriptor => {
        const target = this.lessons.find(x => x.id === l.id);
        if (target && descriptor) {
          this.reviewMap = Object.assign({}, this.reviewMap, {
            [l.id]: descriptor
          });
          m.redraw();
        }
      }
    });
  }

  /**
   * 行内课评入口 (三态同款文案「课评」, 只有图标/颜色不同 —— WCAG 1.4.1)。
   *
   * @param {object} l      课次行
   * @param {boolean} stop  移动端需要 stopPropagation (整张卡片可点开展开)
   */
  reviewEntry(l, stop) {
    // 🔴 v0.18.0 学员只读入口 (辉哥 10/18 拍板) —— 写权限侧的**三态逻辑一个字都没改**:
    //   下面 `canReviewRow()` 为真的分支 (教练) 与 v0.18.0 首版逐字节相同,
    //   只读分支只在**拿不到写位**时才会被问到 (教练走不到这里, readonlyReviewEntry 头一行就 return)。
    if (!this.canReviewRow(l)) return this.readonlyReviewEntry(l, stop);
    const d = this.reviewOf(l);
    const cls = 'ZS-action-link review ' + stateClassFor(d && d.status);
    return m("span", {
      className: cls,
      title: d && d.status === 'draft' ? '课评草稿（仅自己可见）' : '课评',
      onclick: e => {
        if (stop) e.stopPropagation();
        this.openReview(l);
      }
    }, m("i", {
      className: iconClassFor(d && d.status),
      style: "margin-right:3px;"
    }), "\u8BFE\u8BC4");
  }

  /** 已发布态的行内星级 (从未写/草稿都**不**显示行内星级) */
  reviewStars(l) {
    const d = this.reviewOf(l);
    if (!d || d.status !== 'published') return null;
    return m("span", {
      className: "ZS-review-inline-stars"
    }, starsFor(d.rating));
  }

  // ── v0.18.0 学员端只读入口 (辉哥 10/18 拍板, 本轮补的缺口) ──────────────

  /**
   * 学员 (无写位) 的「查看课评」入口。
   *
   * ## 缺口是怎么来的
   *   首版只按**写**位发三态请求 (loadReviewStates 的 `canReviewLessons()` 门控),
   *   入口也只按 `canReviewRow()` (写位) 渲染 ⇒ 学员端既拿不到三态, 也没有任何入口,
   *   只读 modal 明明已经支持 (`readonly` 参数) 却**没有任何调用方**够得着。
   *
   * ## 文案为什么是「查看课评」而不是「课评」(辉哥拍板)
   *   三态入口全叫「课评」是 **WCAG 1.4.1** 的约束 (三态只能靠图标区分, 见 LessonReviews.js),
   *   那条约束针对的是**写权限侧**的三态。学员侧根本没有三态 ——
   *   只有「有已发布课评 / 没有」两种, 而「课评」在学员语境里暗示「我要去写」,
   *   点开却是只读的 ⇒ 用「查看课评」把「看」和「写」在文案上分开。
   *   学员侧只有一种可点样式, 因此不涉及三态区分, 不违反 1.4.1。
   *
   * ## ⛔ 判据逐条
   *   1. 有写位 ⇒ 一律 return null (员工永远走三态入口, 两条路径不共存)
   *   2. `status !== 'completed'` ⇒ 不渲染 (规则 1 同款判据; 服务端 studentDescriptor 也已过滤)
   *   3. 非 `published` (从未写 / 教练有草稿) ⇒ 不渲染 (规则 6 + R2: 草稿对学员 ≡ 不存在)
   */
  readonlyReviewEntry(l, stop) {
    if (this.canReviewLessons()) return null; // ① 员工视角不走这条
    if (!l || l.status !== 'completed') return null; // ② 只在已消课的课次上
    const d = this.reviewOf(l);
    if (!d || d.status !== 'published') return null; // ③ 未发布一律无入口

    return m("span", {
      className: 'ZS-action-link review ' + stateClassFor(d.status),
      title: "\u67E5\u770B\u6559\u7EC3\u53D1\u5E03\u7684\u8BFE\u8BC4\uFF08\u53EA\u8BFB\uFF09",
      onclick: e => {
        if (stop) e.stopPropagation();
        this.openReviewReadonly(l);
      }
    }, m("i", {
      className: iconClassFor(d.status),
      style: "margin-right:3px;"
    }), "\u67E5\u770B\u8BFE\u8BC4");
  }

  /** 打开**只读**课评 modal。⛔ 不传 onChange: 学员改不了, 没有需要回写的行状态。 */
  openReviewReadonly(l) {
    openReviewModal({
      lesson: l,
      descriptor: this.reviewOf(l),
      readonly: true
    });
  }

  // v0.5.5 — 父组件异步换校区时, 同步 attrs + 重新 load (跟 CardTemplatesTab.onbeforeupdate 同款)
  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);
    if (vnode.attrs.activeSchoolId !== this.activeSchoolId) {
      this.activeSchoolId = vnode.attrs.activeSchoolId;
      // v0.17.49 — 换校区时丢弃已选教练: 候选教练是**按生效校区作用域**下发的,
      //   换校区后原来那个教练可能根本不在候选里 (fetchPage 里还有一道兜底,
      //   但这里先清掉可以少发一次注定是空结果的请求)。
      this.coachFilter = '';
      this.load();
    }
  }

  /* ═══════════════════════ 数据加载 (v0.17.18 分页 + 后端全量搜索) ═══════════════════════ */

  /**
   * 首屏 / 换校区 / 换搜索词: 清空重取 (offset 归零)
   *
   * ⚠️ 换搜索词**必须**走这里, 不能只改 this.search 就接着旧 offset 往下取 ——
   *   那会让新关键词从旧 offset 继续取, 直接取到别人的页。
   *   this.lessons = [] 立即清空 ⇒ 换词的瞬间屏幕上不留上一个关键词的行。
   *
   * 保留 `load()` 这个名字: onbeforeupdate(换校区) / checkIn 失败兜底 / openLessonEditor
   * 关闭回调等既有调用点全都调它, 语义就是「从头重拉」= loadFirstPage。
   */
  load() {
    return this.loadFirstPage();
  }
  loadFirstPage() {
    this.lessons = [];
    this.offset = 0;
    this.meta = null;
    this.paginatedOnce = false;
    this.loading = true;
    this.disconnectObserver();
    this.redraw();
    return this.fetchPage(0, 'replace');
  }

  /** 当前生效的搜索词 (trim 后; 空串 = 不过滤, 后端回全量) */
  normalizedSearch() {
    return (this.search || '').trim();
  }

  // ═══════════════════ 教练筛选 (v0.17.49) ═══════════════════

  /** 下拉按钮上的文字: 没选 = 「全部教练」; 选了 = 该教练显示名 */
  coachFilterLabel() {
    if (!this.coachFilter) return '全部教练';
    const hit = this.coaches.find(c => String(c.id) === String(this.coachFilter));
    return hit ? hit.display_name : '全部教练';
  }

  /**
   * 选/取消选教练 —— 走 loadFirstPage(offset 归零), 与换搜索词同一条路。
   *
   * ⚠️ 必须走 loadFirstPage 而不是只改 state: 筛选与搜索一样是**后端**过滤,
   *   offset 不归零的话翻页会从旧 offset 继续取, 结果前后两页对不上。
   */
  onCoachFilter(coachId) {
    this.coachFilter = coachId ? String(coachId) : '';
    this.loadFirstPage();
  }

  // ═══════════════════ 时间筛选 + 状态筛选 (v0.18.33) ═══════════════════
  //
  //   三个筛选项 (教练 / 时间 / 状态) 现在是**同一套范式**:
  //     state ('' = 全部) → label 函数算按钮文字 → onXxx() 里改 state + loadFirstPage()
  //   ⇒ 三者在 fetchPage 里各加一行参数, 互不影响, 可任意叠加。

  /** 时间下拉按钮上的文字: 没选 = 「全部时间」; 选了 = 该项名字 */
  timeFilterLabel() {
    const hit = TIME_FILTERS[this.timeFilter];
    return hit ? hit.label : '全部时间';
  }

  /** 状态下拉按钮上的文字: 没选 = 「全部状态」; 选了 = 该项名字 */
  statusFilterLabel() {
    const hit = STATUS_FILTERS[this.statusFilter];
    return hit ? hit.label : '全部状态';
  }

  /**
   * 选/取消选时间 —— 走 loadFirstPage(offset 归零), 与教练筛选同一条路。
   *
   * ⚠️ 必须走 loadFirstPage 而不是只改 state: 筛选是**后端**过滤,
   *   offset 不归零的话翻页会从旧 offset 继续取, 结果前后两页对不上。
   */
  onTimeFilter(key) {
    this.timeFilter = key || '';
    this.loadFirstPage();
  }

  /** 选/取消选状态 —— 同上 */
  onStatusFilter(status) {
    this.statusFilter = status || '';
    this.loadFirstPage();
  }

  /**
   * 搜索框输入 —— v0.17.18 真正实现
   *
   * ⚠️ 这里**不**调 m.redraw(): 输入框是受控的 (value={this.search}), 每次按键都 redraw
   *   既多余又可能把光标位置/输入法拼音候选框打断。等 debounce 到了再由 loadFirstPage 统一重绘。
   */
  onSearchInput(value) {
    this.search = value;
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this._searchTimer = null;
      this.loadFirstPage(); // offset 归零 + 带新搜索词重发
    }, SEARCH_DEBOUNCE_MS);
  }

  /**
   * 拉一页。
   *
   * @param {number}  offset
   * @param {'replace'|'append'}  mode  replace=替换 (首屏/换筛选) / append=追加 (滚动)
   */
  async fetchPage(offset, mode) {
    // 每次请求领一个递增序号; 响应回来时序号不是最新的就**整个丢弃**。
    // 换搜索词时上一个词的请求还在路上, 不丢的话那批行会被 concat 到新词的尾巴上。
    const seq = ++this.reqSeq;
    try {
      // ⚠️ 每批请求都带**完整**参数 (校区 + 分页 + 搜索词) —— 前端不缓存任何筛选状态给后端猜
      const params = {
        limit: PAGE_SIZE,
        offset: offset
      };
      // ⚠️ school_id 为空时**不传** (跟改造前 `this.activeSchoolId ? {...} : {}` 同款),
      //   传一个 null 会被 mithril 序列化成 'school_id=null', 后端 (int) 转成 0 → where school_id=0 → 全空
      if (this.activeSchoolId) params.school_id = this.activeSchoolId;
      // v0.17.49 — 教练筛选进请求参数 (空串 = 不传, 与 school_id 同款处理:
      //   传 null 会被序列化成 'coach_id=null', 后端 (int) 转 0 → where coach_id=0 → 全空)
      if (this.coachFilter) params.coach_id = this.coachFilter;
      // v0.18.33 — 状态筛选 (空串 = 不传, 与 coach_id 同款处理)
      //   取值是 lessons.status 列的真实取值 (scheduled / checked-in / completed),
      //   后端 ListLessonsPageController 已经支持 status 参数 ⇒ **纯前端接线**, 后端零改动。
      if (this.statusFilter) params.status = this.statusFilter;
      // v0.18.33 — 时间筛选: 区间由**浏览器本地时区**算好再传 (后端只按字面量过滤)
      //   ⚠️ 必须在**前端**算: 存的是本地时间字面量, 而服务器 PHP date() 是 UTC,
      //      后端自己算「今天」会差 8 小时 (详见模块顶部注释 + 后端 v0.18.33 那段)。
      if (this.timeFilter) {
        const range = localDateRange(this.timeFilter);
        if (range) {
          params.start = range.start;
          params.end = range.end;
        }
      }
      const q = this.normalizedSearch();
      // 空搜索词不传 ⇒ 后端不过滤 (回到全量)。搜索与分页是**两个独立参数**, 一起发, 不用前端过滤
      if (q) params.q = q;
      const res = await Api.get('lessons/page', params);
      // ★ 过期响应直接扔掉, 一个字段都不碰
      if (seq !== this.reqSeq) return;
      const rows = res && res.data || [];
      this.meta = res && res.meta || null;
      // v0.17.49 — 教练筛选权限 + 候选列表, 由后端下发。
      //   ⚠️ 无权限态的 emptyMeta 也会带这两个键 (false / []), 所以这里不需要兜底分支;
      //      仍用 `=== true` 严格判, 避免后端某天漏发这个键时误把筛选框放出来。
      this.canFilterCoach = (this.meta && this.meta.can_filter_coach) === true;
      this.coaches = this.meta && Array.isArray(this.meta.coaches) ? this.meta.coaches : [];
      // 换了校区 / 换了权限态之后, 之前选的教练可能已经不在候选里 ⇒ 主动丢弃选择,
      // 否则请求会带着一个后端根本不认的 coach_id, 结果恒为空且用户看不出原因。
      if (this.coachFilter && !this.coaches.some(c => String(c.id) === String(this.coachFilter))) {
        this.coachFilter = '';
      }
      this.lessons = mode === 'replace' ? rows : this.lessons.concat(rows);
      // v0.18.0 — 课评三态跟着这一批一起拉 (追加页里有**新**的课次, 不拉就全是「从未写」)
      this.loadReviewStates(rows);
      // 下一批 offset = 本批起点 + 本批实际行数 (用行数而不是 limit, 最后一页不满 20 时才对)
      this.offset = offset + rows.length;
      if (mode === 'append') this.paginatedOnce = true;

      // ★ 头像预塞 store —— **每批都跑**, 不只是首屏:
      //   追加页带着**新**的学员/教练 id, 不预塞 ⇒ 这些人的 Avatar 是空白 (坑③)
      this.prefetchUsers(rows);
    } catch (e) {
      if (seq !== this.reqSeq) return;
      console.error('[zs] lessons page load failed', e);
      if (mode === 'replace') this.lessons = [];
    } finally {
      // ⚠️ 只有最新那次请求才允许改 loading 标志 —— 被丢弃的旧请求如果也去改,
      //   会把新一轮请求的 loading 提前清掉, 造成重复触发。
      if (seq === this.reqSeq) {
        this.loading = false;
        this.loadingMore = false;
      }
      this.redraw();
      this.syncObserver();
    }
  }

  /**
   * 预 fetch 学员 + 教练用户进 store, 让 Avatar 能拿到 Flarum User model
   *
   * (跟 StudentsListTab v0.5.8 L104-112 / 改造前 load() L60-75 同款。
   *  User model 的 displayName() / avatarUrl() 不能 JSON 序列化, 必须走 store;
   *  后端 LessonSerializer 返的 student/coach 是 plain object, 不 push 进 store Avatar 画不出头像。)
   */
  prefetchUsers(rows) {
    if (!rows || rows.length === 0) return;
    const userIds = new Set();
    rows.forEach(l => {
      if (l.student && l.student.id) userIds.add(l.student.id);
      if (l.coach && l.coach.id) userIds.add(l.coach.id);
    });
    if (userIds.size === 0) return;
    Promise.all(Array.from(userIds).map(uid => app.store.find('users', uid).catch(() => null))).then(() => {
      this.redraw();
    });
  }

  /** 筛选条件下的总条数 —— 一律读 meta.total, **不再**用 this.lessons.length (会随滚动往上跳) */
  totalCount() {
    return this.meta ? Number(this.meta.total) || 0 : 0;
  }

  /* ═══════════════════════ IntersectionObserver (现在共用 utils/InfiniteScroll.js) ═══════════════════════ */
  //
  //   findSentinel / findScrollRoot / disconnectObserver / syncObserver / scheduleSync / loadNextPage
  //   六个方法已挪到共用基类 utils/InfiniteScroll.js (批 41 · 段①)。
  //   三个坑 (滚动根误选 body / m.redraw 不同步 ⇒ observer 建不出来 / 孤儿 observer 泄漏)
  //   的实测根因注释全部随方法一起搬进了那个文件 —— 改滚动逻辑**只改那一处**。
  //
  //   本文件与滚动有关的私有数据只剩两个: this.sentinelAttr (见 oninit) + this._sid (DOM 认领 id)。

  formatTime(dt) {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleString('zh-CN', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // v0.8.4 — 计算课程实际时长 (分钟), 用 start_at / end_at 反推
  //   替代原硬编码 "60 分钟" 文案 (辉哥 10/6 拍板, 决策⑥ 格宽动态跟随校区 SCD)
  //   实际数据库存的 end_at - start_at 就是真实课时, 不需要从 SCD 读
  //
  // ── v0.17.23 改口径: 优先读 card_minutes 快照 (辉哥 10/13 23:30 拍板) ──────
  //   改前这里恒用 end_at - start_at。但 v0.17.5 之后 start_at/end_at 表达的是
  //   **占位时间区块** (「不管它课时多长, 都是占一个时间区块」), 生产恒为 60 分钟,
  //   于是 30 分钟的体验卡被显示成 60 分钟 —— 显示的不是这节课有多长。
  //   card_minutes 是建课那一刻抄下的卡模板 minutes, 才是真实时长。
  //
  //   ⚠️ start_at/end_at 本身一个字都没改, 课表日历继续用它排区块 (辉哥拍板)。
  //      本函数只改「预约管理」这一列的读数来源。
  //
  //   ⚠️ 老数据 card_minutes 为 null ⇒ 回落到 end_at - start_at (老行为逐像素一致)。
  //      `> 0` 那个判断不能省: 万一后端返 0, 显示「0 分钟」比显示 60 更糟。
  formatDuration(l) {
    if (!l) return '';
    if (l.card_minutes !== null && l.card_minutes !== undefined) {
      const cm = Number(l.card_minutes);
      if (cm > 0) return cm + ' 分钟';
    }
    if (!l.start_at || !l.end_at) return '';
    const s = new Date(l.start_at);
    const e = new Date(l.end_at);
    const min = Math.round((e.getTime() - s.getTime()) / 60000);
    if (!min || min < 1) return '';
    return min + ' 分钟';
  }

  // v0.17.23 (BUG-2) — 这节课该显示的「人名」
  //   改前两处都写死 `l.student ? ... : `学员 #${l.student_id}``。体验课的
  //   student_id 是 NULL ⇒ JS 把 null 插值成**字面量 "null"** ⇒ 页面出现
  //   「学员 #null」。JS 模板字符串不会自动躲开 null, 这是个安静到没人会测的坑。
  //   客户名一直在 sales_followups.customer_name 里, v0.17.23 后端已带出来
  //   (LessonSerializer.customer_name)。
  //
  //   兜底顺序: student 对象 → 跟进记录客户名 → 有 student_id 才拼「学员 #id」
  //              → 连 id 都没有 (体验课且跟进记录没填名字) 显示「体验客户」
  //   最后那档跟 CalendarTab 的既有文案对齐 (同款口径, 两处不能各说各话)。
  //   ⚠️ 「有 student_id 才拼」这条是关键: 它才是真正堵死「学员 #null」的那一步,
  //      光加 customer_name 只是恰好盖住了体验课这一种情况。
  studentLabel(l) {
    if (!l) return '';
    if (l.student) return l.student.display_name || l.student.username || '';
    if (l.customer_name) return l.customer_name;
    if (l.student_id !== null && l.student_id !== undefined && l.student_id !== '') {
      return '学员 #' + l.student_id;
    }
    return '体验客户';
  }
  statusBadge(status) {
    const map = {
      scheduled: {
        label: '已预约',
        class: 'scheduled'
      },
      confirmed: {
        label: '已确认',
        class: 'confirmed'
      },
      'checked-in': {
        label: '已签到',
        class: 'checked-in'
      },
      completed: {
        label: '已消课',
        class: 'completed'
      },
      cancelled: {
        label: '已取消',
        class: 'cancelled'
      }
    };
    return map[status] || {
      label: status,
      class: 'scheduled'
    };
  }
  async checkIn(lesson) {
    // v0.7.8j — in-place UI update (辉哥 10/3 23:10 反馈)
    //   不调 this.load() 重新拉全部 lessons, 直接 mutate local lesson.status + m.redraw()
    //   整个 view() 重渲染 → 单行 status 立即变 + 按钮 (签到/消课/取消) 自动按新 status 重排
    //   API 失败 (catch) 仍 fallback load() 兜底 (后端/前端不一致时)
    try {
      await Api.update('lessons', lesson.id, {
        status: 'checked-in'
      });
      this.updateLessonStatus(lesson.id, 'checked-in');
    } catch (e) {
      console.error('checkIn failed', e);
      await this.load();
    }
  }
  async complete(lesson) {
    try {
      const r = await Api.update('lessons', lesson.id, {
        status: 'completed'
      });
      // v0.8.5 — 消课成功后, 同步扣减对应卡的余额 + consumed_count (辉哥 10/7 拍板, 决策① 消课扣)
      //   改前: 只 in-place mutate lesson.status, 卡的余额不变 → 用户看到「卡剩 10 节」点了消课, 卡还是「剩 10 节」
      //   改后: 后端 PATCH 返回 attributes.remaining_lessons + attributes.consumed_count,
      //         前端同步更新 (response fallback: 全量 load() 兜底)
      const attrs = r && r.data && r.data.attributes;
      this.updateLessonStatus(lesson.id, 'completed');
      if (lesson.student_card && attrs) {
        if (typeof attrs.remaining_lessons === 'number') lesson.student_card.remaining_lessons = attrs.remaining_lessons;
        if (typeof attrs.consumed_count === 'number') lesson.student_card.consumed_count = attrs.consumed_count;
        m.redraw();
      } else if (!attrs) {
        // 响应格式不对, fallback 全量 reload
        await this.load();
      }
    } catch (e) {
      console.error('complete failed', e);
      await this.load();
    }
  }
  async cancel(lesson) {
    if (!confirm('确认取消这节课?')) return;
    try {
      await Api.update('lessons', lesson.id, {
        status: 'cancelled'
      });
      this.updateLessonStatus(lesson.id, 'cancelled');
    } catch (e) {
      console.error('cancel failed', e);
      await this.load();
    }
  }

  // v0.7.8j — in-place status 更新 (辉哥 10/3 23:10 反馈, 不要刷新整个页面)
  //   直接改 this.lessons 数组里的 lesson.status + m.redraw(), 整个 view 重渲染
  //   按钮按新 status 自动重排 (render 函数读 l.status), 状态徽章也更新
  //   学员过滤 (v0.7.8h actorRole) 不需要重算 — in-memory 操作
  updateLessonStatus(lessonId, newStatus) {
    const idx = this.lessons.findIndex(l => l.id === lessonId);
    if (idx !== -1) {
      this.lessons[idx].status = newStatus;
      m.redraw();
    } else {
      // 找不到 lesson (理论上不应该), fallback load
      this.load();
    }
  }

  // v0.6.15 — A 方案 (辉哥 22:36 拍板): 检测 mobile viewport 决定 mobile/desktop DOM 分支
  isMobile() {
    return typeof window !== 'undefined' && window.innerWidth <= 640;
  }

  // v0.6.15 — toggle mobile lesson card 展开/收起 (辉哥 22:36 拍板 A 方案: card click 展开签到/消课/取消)
  toggleLessonExpand(lessonId) {
    if (this.expandedLessons.has(lessonId)) {
      this.expandedLessons.delete(lessonId);
    } else {
      this.expandedLessons.add(lessonId);
    }
    m.redraw();
  }

  // v0.6.64 — 学员头像渲染 (辉哥 9/30 23:03 拍板: 全部用户头像改 Flarum Avatar)
  //   优先拿 store 里的 Flarum User model (load() 已预 fetch), 没拿到就 fallback 字母头像
  //   Flarum Avatar 默认不渲染 <a> (点击不跳用户页), 默认无 border (无 box-shadow/outline)
  //   复用 .ZS-avatar 容器 (LESS L4610 在 .ZS-lesson-card-user 下覆盖 22x22 圆形), 容器无 border ✅
  //
  // v0.18.8 — 泛化出 `fallbackChar` (默认 '学'), 桌面「教练」列复用同一套渲染。
  //   ⚠️ 只在**两处 fallback** 上生效: (a) 入参为空时的占位字, (b) store 没 model 时的首字。
  //      学员侧两个调用方都不传第二个参数 ⇒ 默认 '学' ⇒ **学员路径逐字节不变** (辉哥 9/30 拍板过, 不可动)。
  //   为什么泛化而不是复制一份 coachAvatar(): 这段逻辑的三个易错点(store 查找 / 空入参 / 首字取法)
  //      已经踩过一次坑, 复制第二份必然漂; 泛化后新增的只是一个默认参数。
  studentAvatar(student, fallbackChar) {
    if (fallbackChar === void 0) {
      fallbackChar = '学';
    }
    if (!student) {
      return m("span", {
        className: "ZS-avatar"
      }, fallbackChar);
    }
    const userModel = app.store.getById('users', student.id);
    if (userModel) {
      return m("span", {
        className: "ZS-avatar",
        style: "padding:0;overflow:hidden;"
      }, m(Avatar, {
        user: userModel
      }));
    }
    // store 里没 model (用户被删 / 接口异常), fallback 字母头像
    const letter = (student.display_name || student.username || fallbackChar).charAt(0);
    return m("span", {
      className: "ZS-avatar"
    }, letter);
  }

  // v0.6.42 — 课程类型 id 取值 (辉哥 9/30 8:37 反馈, 优先 lesson 表, fallback 卡 template)
  //   lesson 表 lesson_type_id 是单值 string (v0.6.42 起持久化)
  //   fallback 到 student_card.template.lesson_type_id (卡 template 默认配置)
  //   都 NULL 时返 null, 渲染层不显示 tag (跟 LessonsTab 6 处 LessonOptions.labelFor 调用配合)
  lessonTypeIdOf(l) {
    return l.lesson_type_id || l.student_card && l.student_card.template && l.student_card.template.lesson_type_id || null;
  }

  // v0.6.42 — 课程分类 ids 数组 (辉哥 9/30 8:37 反馈)
  //   lesson 表 lesson_category_id 是单值 string, 这里包装成数组 (跟卡 template.lesson_category_ids JSON 数组统一处理)
  //   fallback 到 student_card.template.lesson_category_ids JSON 数组 (v0.6.5a 起卡 template 是多选)
  //   都 null 时返 [] (空数组, 渲染层 .length === 0 不渲染)
  lessonCategoryIdsOf(l) {
    if (l.lesson_category_id) return [l.lesson_category_id];
    if (l.student_card && l.student_card.template && Array.isArray(l.student_card.template.lesson_category_ids)) {
      return l.student_card.template.lesson_category_ids;
    }
    return [];
  }

  // v0.6.35 — "+ 新建预约" 按钮 onclick 入口 (辉哥 9/29 22:34 反馈)
  // v0.6.37 — 改调 utils/BookingModalHelper.openBookingModal (辉哥 9/29 23:46 反馈, DRY)
  //   复用 LessonEditorModal mode='create' (LessonEditorModal 默认 mode='create', 无 lesson)
  //   跟 CalendarTab.openBooking 共享 helper, LessonsTab 不传 start/end/room (默认 now() + 空白)
  openLessonEditor() {
    openBookingModal({
      activeSchoolId: this.activeSchoolId,
      // v0.18.35 (辉哥 10/20 拍板) — 「+ 新建预约」入口**没有**指定某一天 (不像 fc 课表
      //   是点某一格进来的), 所以日期**可以选**。显式传 false 解锁。
      //   ⚠️ helper 那边默认是 true (锁死), 这里必须显式写 false 才会出现日期选择器。
      lockDate: false,
      onClose: () => this.load(),
      // v0.19.16 — 学员分流用: 约完 / 取消之后重新拉一次列表。
      //   ⚠️ 这个入口**不传 date** ⇒ 学员版弹窗默认「今天」 (那个入口没有指定某一天)。
      onBooked: () => {
        this.load();
      }
    });
  }
  view() {
    // v0.17.18 — 总数一律读 meta.total (后端全量口径), 不再用 this.lessons.length
    //   (分页后 lessons.length 是「已加载条数」, 会随滚动一路往上跳 20→40→…)
    const total = this.totalCount();
    const searching = !!this.normalizedSearch();
    const isMobile = this.isMobile();
    // ── v0.18.33: 计数文案跟着**实际生效的时间筛选**走 ──────────────────────
    //   ⚠️ 改前恒为「本周共 N 节课」, 而 meta.total 是**当前筛选后**的全量口径
    //      (v0.17.18 起 meta.total = 「筛选后的全集」, 见下面那段注释)。
    //      以前没有真筛选, 写死「本周」顶多是「不准确」; 现在时间筛选**真的生效**了,
    //      选「本月」却显示「本周共 N 节课」= 当场自相矛盾 ⇒ 文案必须跟着筛选走。
    //   ⇒ 无时间筛选时不再硬写「本周」(那从来就不是真口径), 改成中性的「共 N 节课」。
    const timeScopeText = this.timeFilter && TIME_FILTERS[this.timeFilter] ? TIME_FILTERS[this.timeFilter].label : '';
    return m("div", null, m("div", {
      className: "ZS-toolbar"
    }, m("div", {
      className: "ZS-toolbar-left ZS-lessons-toolbar-left"
    }, m("div", {
      className: "ZS-search-wrap"
    }, m("i", {
      className: "fas fa-search ZS-search-icon",
      "aria-hidden": "true"
    }), m("input", {
      className: "ZS-search-input",
      placeholder: app.translator.trans('ziven-scheduler.forum.lessons.search-placeholder'),
      value: this.search,
      oninput: e => {
        this.onSearchInput(e.target.value);
      }
    })), m(Dropdown, {
      buttonClassName: "ZS-filter-dropdown-btn",
      icon: "far fa-clock",
      label: this.timeFilterLabel()
    }, m(Button, {
      active: !this.timeFilter,
      onclick: () => this.onTimeFilter('')
    }, "\u5168\u90E8\u65F6\u95F4"), m(Button, {
      active: this.timeFilter === 'today',
      onclick: () => this.onTimeFilter('today')
    }, "\u4ECA\u5929"), m(Button, {
      active: this.timeFilter === 'week',
      onclick: () => this.onTimeFilter('week')
    }, "\u672C\u5468"), m(Button, {
      active: this.timeFilter === 'month',
      onclick: () => this.onTimeFilter('month')
    }, "\u672C\u6708")), this.canFilterCoach && m(Dropdown, {
      buttonClassName: "ZS-filter-dropdown-btn ZS-lessons-toolbar-extra",
      icon: "fas fa-user-tie",
      label: this.coachFilterLabel()
    }, m(Button, {
      active: !this.coachFilter,
      onclick: () => this.onCoachFilter('')
    }, "\u5168\u90E8\u6559\u7EC3"), this.coaches.map(c => m(Button, {
      key: c.id,
      active: String(this.coachFilter) === String(c.id),
      onclick: () => this.onCoachFilter(c.id)
    }, c.display_name))), m(Dropdown, {
      buttonClassName: "ZS-filter-dropdown-btn ZS-lessons-toolbar-extra",
      icon: "fas fa-filter",
      label: this.statusFilterLabel()
    }, m(Button, {
      active: !this.statusFilter,
      onclick: () => this.onStatusFilter('')
    }, "\u5168\u90E8\u72B6\u6001"), m(Button, {
      active: this.statusFilter === 'scheduled',
      onclick: () => this.onStatusFilter('scheduled')
    }, "\u5DF2\u9884\u7EA6"), m(Button, {
      active: this.statusFilter === 'checked-in',
      onclick: () => this.onStatusFilter('checked-in')
    }, "\u5DF2\u7B7E\u5230"), m(Button, {
      active: this.statusFilter === 'completed',
      onclick: () => this.onStatusFilter('completed')
    }, "\u5DF2\u6D88\u8BFE")), m("button", {
      className: "ZS-btn secondary ZS-lessons-more-btn"
    }, "\u66F4\u591A", m("i", {
      className: "fas fa-chevron-down",
      "aria-hidden": "true"
    }))), m("div", {
      className: "ZS-toolbar-right"
    }, m("span", {
      className: "ZS-total-text"
    }, searching ? `搜索到 ${total} 节课` : timeScopeText ? `${timeScopeText}共 ${total} 节课` : `共 ${total} 节课`, !searching && this.meta && this.meta.total > this.lessons.length && m("span", {
      style: "color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, " (\u5DF2\u52A0\u8F7D ", this.lessons.length, ")")), this.canCheckIn() && m("button", {
      className: "ZS-btn secondary"
    }, "\u6279\u91CF\u7B7E\u5230"), this.canCreateLesson() && m("button", {
      className: "ZS-btn",
      onclick: () => this.openLessonEditor()
    }, "+ \u65B0\u5EFA\u9884\u7EA6"))), this.loading ? m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D...") : isMobile ?
    // v0.6.15 — mobile: card 列表 + 折叠展开 (辉哥 22:36 拍板 A 方案)
    m("div", {
      className: "ZS-lesson-list-mobile"
    }, this.lessons.map(l => {
      const sb = this.statusBadge(l.status);
      const isExpanded = this.expandedLessons.has(l.id);
      return m("div", {
        className: 'ZS-lesson-card-mobile' + (isExpanded ? ' expanded' : ''),
        key: l.id,
        onclick: () => this.toggleLessonExpand(l.id)
      }, m("div", {
        className: "ZS-lesson-card-row1"
      }, m("span", null, m("span", {
        className: "ZS-lesson-card-time"
      }, this.formatTime(l.start_at)), this.formatDuration(l) && m("span", {
        className: "ZS-lesson-card-time-duration"
      }, this.formatDuration(l))), m("span", {
        className: 'ZS-status-tag ' + sb.class
      }, sb.label)), m("div", {
        className: "ZS-lesson-card-row2"
      }, m("span", {
        className: "ZS-lesson-card-user"
      }, this.studentAvatar(l.student), this.studentLabel(l)), m("span", {
        className: "ZS-lesson-card-divider"
      }, "|"), m("span", {
        className: "ZS-lesson-card-coach"
      }, this.studentAvatar(l.coach, '教'), m("span", null, l.coach ? l.coach.display_name || l.coach.username : `教练 #${l.coach_id}`)), m("span", {
        className: "ZS-lesson-card-divider"
      }, "|"), m("span", null, l.student_card ? m("span", {
        className: "ZS-card-tag",
        title: l.student_card.name || ''
      }, l.student_card.template?.name || l.student_card.name || '卡片', l.student_card.remaining_lessons != null && l.student_card.remaining_lessons >= 0 && m("span", {
        className: "ZS-card-tag-suffix"
      }, " \xB7 \u5269", l.student_card.remaining_lessons)) : m("span", {
        style: "color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u65E0\u5361\u7247"))), (this.lessonTypeIdOf(l) || this.lessonCategoryIdsOf(l).length > 0) && m("div", {
        className: "ZS-lesson-card-row-types"
      }, this.lessonTypeIdOf(l) && m("span", {
        className: "ZS-type-tag"
      }, m("i", {
        className: "fas fa-tag",
        style: "color:var(--zs-success-22c55e, #22c55e);margin-right:3px;"
      }), LessonOptions.labelFor('type', this.lessonTypeIdOf(l))), this.lessonCategoryIdsOf(l).map(cid => m("span", {
        key: cid,
        className: "ZS-category-tag"
      }, m("i", {
        className: "fas fa-bookmark",
        style: "color:var(--zs-purple-8b5cf6, #8b5cf6);margin-right:3px;"
      }), LessonOptions.labelFor('category', cid)))), m("div", {
        className: 'ZS-lesson-mobile-actions' + (isExpanded ? ' expanded' : '')
      }, this.canCheckIn() && l.status === 'scheduled' && m("span", {
        className: "ZS-action-link",
        onclick: e => {
          e.stopPropagation();
          this.checkIn(l);
        }
      }, "\u7B7E\u5230"), this.canCheckIn() && l.status === 'confirmed' && m("span", {
        className: "ZS-action-link",
        onclick: e => {
          e.stopPropagation();
          this.checkIn(l);
        }
      }, "\u7B7E\u5230"), this.reviewStars(l), this.reviewEntry(l, true), this.canCompleteLesson() && l.status !== 'completed' && l.status !== 'cancelled' && m("span", {
        className: "ZS-action-link",
        onclick: e => {
          e.stopPropagation();
          this.complete(l);
        }
      }, "\u6D88\u8BFE"), this.canCancelLesson() && l.status !== 'cancelled' && m("span", {
        className: "ZS-action-link danger",
        onclick: e => {
          e.stopPropagation();
          this.cancel(l);
        }
      }, "\u53D6\u6D88"), l.status === 'cancelled' && m("span", {
        style: "color:var(--zs-text-2-9ca3af, #9ca3af);font-size:13px;"
      }, "\u8BE6\u60C5")));
    }), this.lessons.length === 0 && m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u6682\u65E0\u8BFE\u7A0B")) :
    // desktop: 7 列 grid (v0.6.41 加 "课程" 列: 课程类型 + 课程分类, 辉哥 9/30 8:20 反馈)
    m("div", {
      className: "ZS-table"
    }, m("div", {
      className: "ZS-table-header"
    }, m("div", null, "\u65F6\u95F4"), m("div", null, "\u5B66\u5458"), m("div", null, "\u6559\u7EC3"), m("div", null, "\u4F7F\u7528\u5361\u7247"), m("div", null, "\u8BFE\u7A0B"), m("div", null, "\u72B6\u6001"), m("div", null, "\u64CD\u4F5C")), this.lessons.map(l => {
      const sb = this.statusBadge(l.status);
      return m("div", {
        className: "ZS-table-row",
        key: l.id
      }, m("div", {
        className: "ZS-time-cell"
      }, m("div", {
        className: "ZS-time-primary"
      }, this.formatTime(l.start_at)), this.formatDuration(l) && m("div", {
        className: "ZS-time-duration"
      }, this.formatDuration(l))), m("div", {
        className: "ZS-user-cell"
      }, this.studentAvatar(l.student), m("span", null, this.studentLabel(l))), m("div", {
        className: "ZS-coach-cell"
      }, this.studentAvatar(l.coach, '教'), m("span", null, l.coach ? l.coach.display_name || l.coach.username : `教练 #${l.coach_id}`)), m("div", null, l.student_card ? m("span", {
        className: "ZS-card-tag",
        title: l.student_card.name || ''
      }, l.student_card.template?.name || l.student_card.name || '卡片', l.student_card.remaining_lessons != null && l.student_card.remaining_lessons >= 0 && m("span", {
        className: "ZS-card-tag-suffix"
      }, " \xB7 \u5269", l.student_card.remaining_lessons)) : m("span", {
        style: "color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u2014")), m("div", null, this.lessonTypeIdOf(l) || this.lessonCategoryIdsOf(l).length > 0 ? m('[', null, this.lessonTypeIdOf(l) && m("span", {
        className: "ZS-type-tag"
      }, m("i", {
        className: "fas fa-tag",
        style: "color:var(--zs-success-22c55e, #22c55e);margin-right:3px;"
      }), LessonOptions.labelFor('type', this.lessonTypeIdOf(l))), this.lessonCategoryIdsOf(l).length > 0 && m("span", {
        className: "ZS-category-tag"
      }, this.lessonCategoryIdsOf(l).map((cid, i, arr) => m("span", {
        key: cid
      }, m("i", {
        className: "fas fa-bookmark",
        style: "color:var(--zs-purple-8b5cf6, #8b5cf6);margin-right:3px;"
      }), LessonOptions.labelFor('category', cid), i < arr.length - 1 ? ' · ' : '')))) : m("span", {
        style: "color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u2014")), m("div", null, m("span", {
        className: 'ZS-status-tag ' + sb.class
      }, sb.label)), m("div", {
        className: "ZS-action-cell"
      }, this.canCheckIn() && l.status === 'scheduled' && m("span", {
        className: "ZS-action-link",
        onclick: () => this.checkIn(l)
      }, "\u7B7E\u5230"), this.canCheckIn() && l.status === 'confirmed' && m("span", {
        className: "ZS-action-link",
        onclick: () => this.checkIn(l)
      }, "\u7B7E\u5230"), this.reviewStars(l), this.reviewEntry(l, false), this.canCompleteLesson() && l.status !== 'completed' && l.status !== 'cancelled' && m("span", {
        className: "ZS-action-link",
        onclick: () => this.complete(l)
      }, "\u6D88\u8BFE"), this.canCancelLesson() && l.status !== 'cancelled' && m("span", {
        className: "ZS-action-link danger",
        onclick: () => this.cancel(l)
      }, "\u53D6\u6D88"), l.status === 'cancelled' && m("span", {
        style: "color:var(--zs-text-2-9ca3af, #9ca3af);"
      }, "\u8BE6\u60C5")));
    }), this.lessons.length === 0 && m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u6682\u65E0\u8BFE\u7A0B")), this.loadingMore ? m("div", {
      style: "padding:20px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D...") : this.meta && this.meta.has_more ? m("div", {
      "data-zlessonsid": this._sid,
      style: "height:1px;"
    }) : this.paginatedOnce ? m("div", {
      style: "padding:20px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u5DF2\u5168\u90E8\u52A0\u8F7D") : null);
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/LessonsTab', LessonsTab);